import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { 
  FileText, 
  ArrowLeft, 
  Save,
  User,
  Calendar,
  Clock,
  MapPin,
  AlertCircle,
  Building2,
  Home,
  X,
  CalendarDays,
  Upload,
  Building as BuildingOffice2Icon
} from 'lucide-react';

import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
// RadioGroup component not available, using HTML radio inputs
import { Separator } from '../../components/ui/separator';
import { useToast } from '../../components/ui/use-toast';

import { useAuthStore } from '../../store/authStore';
import { api, endpoints } from '../../services/api';
import Ek3DocumentUpload from '../../components/Ek3DocumentUpload';
import { useHospitalAddresses } from '../../hooks/useLookups';

// Hasta adresleri ve hastane listesi artık /api/lookups/addresses endpoint'inden çekiliyor.
// useHospitalAddresses() hook'u (frontend/src/hooks/useLookups.js) bu veriyi sağlar.
// Yönetim için: /lookup-definitions sayfası kullanılır (admin/supervisor erişimi).
const _REMOVED_HOSPITAL_ADDRESS_OPTIONS = [
  'Arnavutköy Devlet Hastanesi',
  'Dr. İsmail Niyazi Kurtulmuş Hastanesi',
  'Özel Avrupa Hospital Hastanesi',
  'Fatih Sultan Mehmet Eğitim Ve Araştırma Hastanesi',
  'Özel Memorial Ataşehir Hastanesi',
  'Özel Ataşehir Florence Nıghtıngale Hastanesi',
  'Özel Ataşehir Hastanesi',
  'Özel Avicenna Hastanesi Ataşehir (Anadolu Çınar)',
  'Özel Bayındır İçerenköy Hastanesi',
  'Özel Eren Hastanesi',
  'Avcılar Murat Kölük Devlet Hastanesi',
  'Özel Avcılar Anadolu Hastanesi',
  'Özel Avcılar Baypark Hastanesi',
  'Özel Avcılar Hospital',
  'Özel Medicana Hospitals Avcılar',
  'Bağcılar Eğitim Ve Araştırma Hastanesi',
  'Özel Medipol Mega Hastaneler Kompleksi',
  'Özel Safa Hastanesi',
  'Özel Yeni İklim Hastanesi',
  'Özel Bağcılar Medilife Hastanesi',
  'Özel Güneşli Erdem Hastanesi',
  'Özel Hastane34 Hastanesi',
  'Özel Hospitalist Hastanesi',
  'Özel İstanbul Medicine Hospital Hastanesi',
  'Bahçelievler Devlet Hastanesi',
  'İstanbul Özel Memorial Hizmet Hastanesi',
  'Özel A Hastanesi',
  'Özel Aile Hastanesi',
  'Özel Ataköy Hastanesi',
  'Özel İstanbul Göz Hastanesi',
  'Özel Median Hastanesi',
  'Özel Medical Park Bahçelievler Hastanesi',
  'Özel Medicana Hospitals Bahçelievler',
  'Özel Medicana Hospitals Haznedar Hastanesi',
  'Özel Memorial Bahçelievler Hastanesi',
  'Özel Nisa Hastanesi',
  'Özel Vital Hospital Hastanesi',
  'Özel Yenibosna Safa Hastanesi',
  'Bakırköy Dr. Sadi Konuk Eğitim Ve Araştırma Hastanesi',
  'Prof. Dr. Mazhar Osman Ruh Sağlığı Ve Sinir Hastalıkları Eğitim Ve Araştırma Hastanesi',
  'İstanbul Eğitim Ve Araştırma Hastanesi Osmaniye / Bakırköy Polikliniği',
  'Lepra Deri Ve Zührevi Hastalıkları Hastanesi',
  'Özel Acıbadem Bakırköy Hastanesi',
  'Özel Acıbadem İnternational Hastanesi',
  'Özel Çamlık Hastanesi',
  'Özel Dünya Göz Hastanesi Ataköy',
  'Özel Ethica İncirli Hastanesi',
  'Özel Yaşar Hastanesi',
  'Başakşehir Çam Ve Sakura Şehir Hastanesi',
  'Başakşehir Devlet Hastanesi',
  'Bayrampaşa Devlet Hastanesi',
  'Özel Baypark Hospital Hastanesi',
  'Özel Bayrampaşa Kolan Hospıtal Hastanesi',
  'Özel Göz Nurunu Koruma Vakfı Bayrampaşa Göz Hasta',
  'Beşiktaş Sait Çiftçi Devlet Hastanesi',
  'Özel Dentistanbul Diş Hastanesi',
  'Özel Dünya Göz Hastanesi Etiler',
  'Özel Estethica Levent Hastanesi',
  'Özel Etiler Hastanesi',
  'Özel Gayrettepe Florance Nightingale Hastanesi',
  'Özel Liv Hospital Ulus Hastanesi',
  'Özel Ota-Jinemed Hastanesi',
  'Beykoz Devlet Hastanesi',
  'Özel Medistate Kavacık Hastanesi',
  'Beylikdüzü Ağız Ve Diş Sağlığı Merkezi',
  'Özel Beylikdüzü Kolan Hospital Hastanesi',
  'Özel Beylikdüzü Medilife Hastanesi',
  'Özel Birinci Göz Hastanesi',
  'Özel Beylikdüzü Medicana Hastanesi',
  'Beyoğlu Göz Eğitim Ve Araştırma Hastanesi',
  'Taksim Eğitim Ve Araştırma Hastanesi',
  'Özel Okmeydanı Hastanesi',
  'Özel Avusturya Sen Jorj Hastanesi',
  'Beylikdüzü Devlet Hastanesi',
  'Büyükçekmece Mimar Sinan Devlet Hastanesi',
  'Özel Büyükçekmece Kolan Hospital Hastanesi',
  'Çatalca İlyas Çokay Devlet Hastanesi',
  'Çekmeköy Devlet Hastanesi',
  'Esenler Kadın Doğum Ve Çocuk Hastalıkları Hastanesi',
  'Özel Avicenna Hastanesi',
  'Özel Esenler Ensar Hastanesi Avicenna',
  'Özel Güney Hastanesi',
  'Esenyurt Necmi Kadıoğlu Devlet Hastanesi',
  'Özel Doğa Hospital Hastanesi',
  'Özel Esencan Hastanesi',
  'Özel Reyap İstanbul Hastanesi',
  'Eyüpsultan Devlet Hastanesi',
  'Özel Alibey Hospital Hastanesi',
  'Haseki Eğitim Ve Araştırma Hastanesi',
  'İstanbul Eğitim Ve Araştırma Hastanesi Samatya Polikliniği',
  'Bezmialem Vakıf Üniversitesi Tıp Fakültesi Hastanesi',
  'İstanbul Üniversitesi Cerrahpaşa Tıp Fakültesi Hastanesi',
  'Özel Balat Or-Ahayim Musevi Hastanesi Vakfı İktisadi İ',
  'Özel Çapa Hastanesi',
  'Özel Çapa Medilife Hastanesi',
  'Özel Fatih Hastanesi',
  'Özel Medikal Park Hospital Hastanesi',
  'Gaziosmanpaşa Eğitim Ve Araştırma Hastanesi',
  'Gaziosmanpaşa Eğitim Ve Araştırma Fizik Tedavi Rehabilitasyon Hastanesi',
  'Özel Avrupa Şafak Hastanesi',
  'Özel Başarı Hastanesi',
  'Özel Bilge Hastanesi',
  'Özel Duygu Hastanesi',
  'Özel Yıldız Academıa Hastanesi',
  'Yeni Yüzyıl Üniversitesi Özel Gaziosmanpaşa Hastanesi',
  'Özel Asya Hastanesi',
  'Özel Avrasya Hastanesi Gaziosmanpaşa',
  'Güngören Ağız Ve Diş Sağlığı Merkezi',
  'Özel Güngören Hastanesi',
  'Özel İlgi Hastanesi',
  'Özel Meltem Hastanesi Ve Doğum Hastanesi',
  'Erenköy Fizik Tedavi Ve Rehabilitasyon Hastanesi',
  'Göztepe Prof. Dr. Süleyman Yalçın Şehir Hastanesi',
  'Erenköy Ruh Ve Sinir Hastalıkları Eğitim Ve Araştırma Hastanesi',
  'Özel Acıbadem Hastanesi',
  'Özel Acıbadem Kozyatağı Hastanesi',
  'Özel Çağıner Hastanesi',
  'Özel Doktor Nazif Bağrıaçık Kadıköy Hastanesi',
  'Özel Göztepe Hastanesi',
  'Özel İstanbul Medipol Hastanesi',
  'Özel Kadıköy Florance Nightingale Hastanesi',
  'Özel Kozyatağı Central Hospital',
  'Özel Medical Park Göztepe Hastanesi Kopmleksi (Genel)',
  'Özel Medicana Kadıköy Hastanesi',
  'Kağıthane Devlet Hastanesi',
  'Özel Avicenna Hastanesi Gültepe',
  'Özel Derindere Hastanesi',
  'Özel Levent Hastanesi',
  'Kartal Dr. Lütfi Kırdar Şehir Hastanesi',
  'Kartal Koşuyolu Yüksek İhtisas Eğitim Ve Araştırma Hastanesi',
  'Özel Vera Hastanesi',
  'Türk Kızılayı Özel Kartal Hastanesi',
  'Özel Avicenna Umut Hastanesi',
  'Özel Birlik Hastanesi',
  'Özel İstanbul İbni Sina Hastanesi',
  'Kanuni Sultan Süleyman Eğitim Ve Araştırma Hastanesi',
  'Mehmet Akif Ersoy Göğüs Kalp Ve Damar Cerrahisi Eğitim Araştırma Hastanesi',
  'Özel Akgün Tem Hastanesi',
  'Özel Batı Bahat Hospital Hastanesi',
  'Özel Doğan Hastanesi',
  'Özel Dr.Sadık Ahmet Hastanesi',
  'Özel Halkalı Kent Hastanesi',
  'Özel İstanbul Hospital',
  'Özel Rumeli Hospital Hastanesi',
  'Süreyyapaşa Göğüs Hastalıkları Ve Göğüs Cerrahisi Eğitim Araştırma Hastanesi',
  'Maltepe Devlet Hastanesi',
  'Fatih Üniversitesi Tıp Fakültesi Maltepe Hastanesi',
  'İstanbul Meslek Hastalıkları Hastanesi',
  'Maltepe Üniversitesi Tıp Fakültesi Hastanesi',
  'Özel Maltepe Bölge ( Dünya) Hastanesi',
  'Özel Maltepe Ersoy Hastanesi',
  'Özel Mehmet Toprak Hastanesi',
  'Özel Delta Hospital',
  'Özel İstanbul Onkoloji Hastanesi',
  'Marmara Universitesi İstanbul Pendik Eğitim Ve Araştırma Hastanesi',
  'Pendik Devlet Hastanesi',
  'Özel Pendik Bölge Hastanesi',
  'Özel Pendik Hospital Hastanesi',
  'Özel Remedy Hospital Hastanesi',
  'Özel Şifa Hastanesi',
  'Özel Vm Medicalpark Pendik Hastanesi',
  'Özel Yüzyıl Hastanesi Pendik',
  'Özel Adatıp Kurtköy Hastanesi',
  'Özel Emsey Hospital Hastanesi',
  'Özel Kurtköy Ersoy Hastanesi',
  'Prof. Dr. Feriha Öz Acil Durum Hastanesi',
  'Sancaktepe Şehit Prof. Dr. İlhan Varank Eğitim Ve Araştırma Hastanesi',
  'Sancaktepe Ağız Ve Diş Sağlığı Merkezi',
  'Özel Via Hospital Sancaktepe',
  'Özel İstanbul Bölge Hastanesi',
  'İstinye Devlet Hastanesi',
  'Sarıyer İsmail Akgün Devlet Hastanesi',
  'Sarıyer Hamidiye Etfal Eğitim Araştırma Çayırbaşı Ek Hizmet Binası',
  'Baltalımanı Metin Sabancı Kemik Hastalıkları Eğitim Araştırma Hastanesi',
  'Özel Acıbadem Maslak Hastanesi',
  'Marmara Ceza İnfaz Kurumu Devlet Hastanesi',
  'Silivri Devlet Hastanesi',
  'Özel Silivri Kolan Hospital Hastanesi',
  'Özel Silivri Medical Park Hastanesi',
  'Sultanbeyli Devlet Hastanesi',
  'Özel Ersoy Hastanesi',
  'Özel Saygı Hastanesi',
  'Lütfiye Nuri Burat Devlet Hastanesi',
  'Özel Bahat Hospital Hastanesi',
  'Özel Gazi Hastanesi',
  'Özel Medigold Sultan Hastanesi',
  'Sile Devlet Hastanesi',
  'Okmeydanı Eğitim Ve Araştırma Hastanesi',
  'Prof. Dr. Cemil Taşcıoğlu Şehir Hastanesi',
  'Sisli Hamidiye Etfal Eğitim Ve Araştırma Hastanesi',
  'Özel Acıbadem Taksim Hastanesi',
  'Özel Dora Hospital Hastanesi',
  'Özel Fransız Lape Hastanesi',
  'Özel İstanbul Cerrahi Hastanesi',
  'Özel İstanbul Florence Nıghtıngale Hastanesi',
  'Özel Kadıoğlu Hastanesi',
  'Özel Mecidiyeköy Çevre Hastanesi',
  'Özel Memorial Şişli Hastanesi',
  'Özel Nişantaşı Hastanesi',
  'Özel Osmanoğlu Hastanesi',
  'Özel Surp Agop Hastanesi (Taksim Vakıf Ermeni)',
  'Özel Şişli Florance Nightingale Hastanesi',
  'Özel Şişli Kolan İnternatıonal Hospital Hastanesi',
  'Özel Türkiye Gazetesi Hastanesi',
  'Özel Vehbi Koç Vakfı Amerikan Hastanesi',
  'Tuzla Devlet Hastanesi',
  'Özel Tuzla Hastanesi',
  'Özel Gsm Gisbir Sağlık Merkezi Hastanesi',
  'Ümraniye Eğitim Ve Araştırma Hastanesi',
  'Özel Hisar Çamlıca Hastanesi',
  'Özel Hisar İntercontinental Hospital Hastanesi',
  'Özel Np İstanbul Beyin Hastanesi',
  'Özel Afiyet Hastanesi',
  'Özel Atlas Hastanesi',
  'Özel Erdem Hastanesi',
  'Üsküdar Devlet Hastanesi',
  'Dr. Siyami Ersek Göğüs Kalp Ve Damar Cerrahisi Eğitim Ve Araştırma Hastanesi',
  'Haydarpaşa Numune Eğitim Ve Araştırma Hastanesi',
  'Sultan 2.Abdüıhamid Han Eğitim Ve Araştırma Hastanesi',
  'Zeynep Kamil Kadın Ve Çocuk Hastalıkları Eğitim Ve Araştırma Hastanesi',
  'Özel Academic Hospital Hastanesi',
  'Özel Acıbadem Altunizade Hastanesi',
  'Özel Çamlıca Erdem Hastanesi',
  'Özel Medıvıa Hospıtal Hastanesi',
  'Özel Medicana Hospitals Çamlıca Hastanesi',
  'Özel Moodist Hastanesi',
  'Özel Üsküdar Hospitaltürk Hastanesi',
  'Özel Üsküdar Anadolu Hastanesi',
  'Özel Yunus Emre Hastanesi',
  'Yedikule Göğüs Hastalıkları Ve Göğüs Cerrahisi Eğitim Araştırma Hastanesi',
  'İstanbul Eğitim Ve Araştırma Hastanesi Zeytinburnu Polikliniği',
  'Süleymaniye Kadın Doğum Ve Çocuk Hastalıkları Eğitim Ve Araştırma Hastanesi',
  'Özel Avrasya Hastanesi',
  'Özel İstanbul Balıklı Rum Hastanesi',
  'Özel Sante Plus Hastanesi',
  'Özel Yedikule Surp Pırgiç Ermeni Hastanesi',
  'Ataşehir Medicana Hastanesi',
  'Beykent Üniversite Hastanesi',
  'Ataköy Medicana Hastanesi',
  'Zincirlikuyu Medicana Hastanesi',
  'Silivri Anadolu Hastanesi',
  'Haribo',
  'Ceren Ofset',
  'Galataport',
  'Kuyumcukent',
  'Botaş',
  'Kumsal Bakımevi',
  'Avrupa Diyaliz Merkezi Arnavutköy',
  'Mall Of Avm',
  'Saadet Gıda',
  'İstanbul Fizik Tedavi Ve Rehabilitasyon Eğitim Ve Araştırma Hastanesi',
  'İstinye Liv Hospital',
  'Arnavutköy Belediyesi',
  'Kale Kayışlar',
  'Erse Kablo',
  'Şenyayla',
  'Elif Plastik',
  'Öztıryakiler',
  'Colins',
  'Güngören Belediyesi',
  'Etra Sağlık Hizmetleri',
  'Seyrantepe Hamidiye Etfal Ek Hizmet Binası(Ek Kampüs)',
  'Özgün Bakımevi',
  'Aymed Hiperbarik Göztepe',
  'Özel Florya Hastanesi',
  'Daviva Diyaliz Ümraniye',
  'Özel Acıbadem Ataşehir Hastanesi',
  'Okan Üniversite Hastanesi',
  'Anadolu Hiperbarik',
  'Murat Dilmener Acil Durum Hastanesi/Cerrahpaşa',
  'Zeytinburnu Avrasya Hastanesi',
  'Dr Sadi Konuk Bahçelievler Diş Hastanesi',
  'Pendik Kaynarca Semt Polikliniği',
  'Asaf Ataseven Ek Hizmet Binası',
  'Özel Medipol Bahçelievler Hastanesi',
  'Acıbadem Atakent Hastanesi',
  'Sabiha Gökçen Hava Limanı',
  'İstinye Üniversitesi Medical Park Gaziosmanpaşa',
  'Koç Üniversite Hastanesi',
  'Dragos Bezmialem Hastanesi',
  'İstinye Üniversitesi Topkapı Sağlık Bilimleri Hastanesi',
  'Kaya Ramada Otel',
  'Tüyap',
  'Meva Bakımevi',
  'Avcılar Oksimer Merkezi',
  'Medicell Tıp Merkezi',
  'Darülaceze',
  'Ev Adresi',
  'Diğer'
]; // Bu sabit artık kullanılmıyor, sadece referans için tutuluyor

// Faturalanılacak kurum seçenekleri - Dinamik olarak institutions tablosundan çekilecek
const NewCasePage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const { toast } = useToast();

  // Hastane/adres listesi veritabanından çekiliyor
  const { data: hospitalAddresses = [] } = useHospitalAddresses();
  
  const isPresidencyUser = user?.role === 'external_presidency';

  // EK-3 upload phase state (for presidency users after case is created)
  const [createdCaseId, setCreatedCaseId] = useState(null);
  const [createdCaseNumber, setCreatedCaseNumber] = useState(null);
  const [ek3Uploaded, setEk3Uploaded] = useState(false);
  const [ek3CaseData, setEk3CaseData] = useState(null);
  const [ek3ToggleOpen, setEk3ToggleOpen] = useState(false);

  // EK-3 inline form states (file selected before case creation)
  const [ek3FormToggleOpen, setEk3FormToggleOpen] = useState(false);
  const [ek3FormFile, setEk3FormFile] = useState(null);
  const ek3FormFileInputRef = useRef(null);

  // Form states
  const [caseGiverType, setCaseGiverType] = useState(isPresidencyUser ? 'presidency' : 'person');
  const [selectedBillingInstitution, setSelectedBillingInstitution] = useState('');
  const [billingInstitutions, setBillingInstitutions] = useState([]);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState('');
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [linkedOrganizationId, setLinkedOrganizationId] = useState('');
  // 112 Başkanlıkları states
  const [selectedPresidency, setSelectedPresidency] = useState(null);
  const [selectedPresidencyHospital, setSelectedPresidencyHospital] = useState('');
  const [presidencyHospitals, setPresidencyHospitals] = useState([]);
  const [allHospitals, setAllHospitals] = useState([]);
  const [presidencies, setPresidencies] = useState([]);
  
  // Institution search states (for autocomplete)
  const [institutionSearch, setInstitutionSearch] = useState('');
  const [showInstitutionDropdown, setShowInstitutionDropdown] = useState(false);
  const [filteredInstitutions, setFilteredInstitutions] = useState([]);
  const [institutionHighlightIndex, setInstitutionHighlightIndex] = useState(-1);
  const institutionDropdownRef = useRef(null);
  
  // Address search states
  const [patientAddressSearch, setPatientAddressSearch] = useState('');
  const [destinationAddressSearch, setDestinationAddressSearch] = useState('');
  const [showPatientDropdown, setShowPatientDropdown] = useState(false);
  const [showDestinationDropdown, setShowDestinationDropdown] = useState(false);
  const [filteredPatientAddresses, setFilteredPatientAddresses] = useState([]);
  const [filteredDestinationAddresses, setFilteredDestinationAddresses] = useState([]);
  const [patientAddressHighlightIndex, setPatientAddressHighlightIndex] = useState(-1);
  const [destinationAddressHighlightIndex, setDestinationAddressHighlightIndex] = useState(-1);

  // Kurumları fetch et (autocomplete için)
  useEffect(() => {
    const fetchInstitutions = async (searchTerm = '') => {
      try {
        const url = searchTerm 
          ? `/institutions?search=${encodeURIComponent(searchTerm)}`
          : '/institutions';
        const response = await api.get(url);
        if (response.data.success) {
          const institutions = response.data.data.map(inst => ({
            id: inst.id,
            name: inst.name,
            code: inst.code
          }));
          if (searchTerm) {
            setFilteredInstitutions(institutions);
          } else {
            setBillingInstitutions(institutions.map(inst => inst.name));
          }
        }
      } catch (error) {
        console.error('Kurumlar yüklenirken hata:', error);
        setFilteredInstitutions([]);
      }
    };
    
    // İlk yüklemede tüm kurumları getir
    if (!institutionSearch) {
      fetchInstitutions();
    }
  }, []);

  // Kurum arama (ilk karakterden itibaren)
  useEffect(() => {
    if (institutionSearch && institutionSearch.length >= 1) {
      const timeoutId = setTimeout(async () => {
        try {
          const response = await api.get(`/institutions?search=${encodeURIComponent(institutionSearch)}`);
          if (response.data.success) {
            const institutions = response.data.data.map(inst => ({
              id: inst.id,
              name: inst.name,
              code: inst.code
            }));
            setFilteredInstitutions(institutions);
            setShowInstitutionDropdown(true);
          }
        } catch (error) {
          console.error('Kurum arama hatası:', error);
          setFilteredInstitutions([]);
        }
      }, 300); // Debounce 300ms

      return () => clearTimeout(timeoutId);
    } else {
      setFilteredInstitutions([]);
      setShowInstitutionDropdown(false);
    }
  }, [institutionSearch]);

  // Dropdown dışına tıklandığında kapat
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (institutionDropdownRef.current && !institutionDropdownRef.current.contains(event.target)) {
        setShowInstitutionDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch hospitals and presidencies for "112 Başkanlıkları" option
  useEffect(() => {
    if (caseGiverType === 'presidency') {
      const fetchHospitalsAndPresidencies = async () => {
        try {
          const [hospitalsRes, institutionsRes] = await Promise.all([
            api.get('/hospitals'),
            api.get('/institutions')
          ]);
          const hospitals = hospitalsRes.data?.data || [];
          const institutions = institutionsRes.data?.data || [];
          setAllHospitals(hospitals);
          // Filter institutions with "BAŞKANLIK" in name
          const presFiltered = institutions.filter(inst =>
            inst.name && inst.name.toUpperCase().includes('BAŞKANLIK')
          );
          setPresidencies(presFiltered);

          // Presidency users choose their own presidency from the dropdown
        } catch (error) {
          console.error('Hastane/Başkanlık yüklenirken hata:', error);
        }
      };
      fetchHospitalsAndPresidencies();
    }
  }, [caseGiverType]);

  // Filter hospitals when presidency changes
  useEffect(() => {
    if (selectedPresidency && allHospitals.length > 0) {
      const filtered = allHospitals.filter(h => h.institution_id === selectedPresidency.id);
      setPresidencyHospitals(filtered);
      setSelectedPresidencyHospital('');
    } else {
      setPresidencyHospitals([]);
    }
  }, [selectedPresidency, allHospitals]);

  // Fetch active/planned organizations
  const { data: activeOrgsResponse } = useQuery({
    queryKey: ['active-organizations'],
    queryFn: () => api.get('/organizations/lookup/active').then(res => res.data),
  });

  const activeOrganizations = activeOrgsResponse?.data || [];

  // Handle organization selection
  const handleOrganizationSelect = (orgId) => {
    setSelectedOrganizationId(orgId);
    const org = activeOrganizations.find(o => String(o.id) === String(orgId));
    setSelectedOrganization(org || null);
    if (org) {
      setValue('organization_id', org.id);
      setValue('case_giver_institution', org.organization_name);
    } else {
      setValue('organization_id', '');
      setValue('case_giver_institution', '');
    }
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch
  } = useForm({
    defaultValues: {
      request_date: new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Istanbul' }),
      request_time: new Date().toLocaleTimeString('tr-TR', { timeZone: 'Europe/Istanbul', hour: '2-digit', minute: '2-digit', hour12: false }),
      requesting_authority: user ? `${user.first_name} ${user.last_name}` : '',
      case_giver_type: isPresidencyUser ? 'presidency' : 'person',
      patient_nationality_type: 'TC'
    }
  });

  // Auto-calculate age/birth year
  const calculateAge = (birthYear) => {
    const currentYear = new Date().getFullYear();
    return currentYear - parseInt(birthYear);
  };

  const calculateBirthYear = (age) => {
    const currentYear = new Date().getFullYear();
    return currentYear - parseInt(age);
  };

  const handleBirthYearBlur = (e) => {
    const birthYear = e.target.value;
    if (birthYear && birthYear.length === 4) {
      const calculatedAge = calculateAge(birthYear);
      if (calculatedAge >= 0 && calculatedAge <= 150) {
        setValue('patient_age', calculatedAge);
      }
    }
  };

  const handleAgeBlur = (e) => {
    const age = e.target.value;
    if (age && parseInt(age) >= 0 && parseInt(age) <= 150) {
      const calculatedBirthYear = calculateBirthYear(age);
      if (calculatedBirthYear >= 1900 && calculatedBirthYear <= new Date().getFullYear()) {
        setValue('patient_birth_year', calculatedBirthYear);
      }
    }
  };

  // Address search functions (hospitalAddresses veritabanından çekiliyor)
  const handlePatientAddressSearch = (e) => {
    const value = e.target.value;
    setPatientAddressSearch(value);
    
    if (value.length >= 3) {
      const filtered = hospitalAddresses
        .map(a => (typeof a === 'string' ? a : a.name))
        .filter(name => name.toLowerCase().includes(value.toLowerCase()));
      setFilteredPatientAddresses(filtered);
      setShowPatientDropdown(true);
    } else {
      setFilteredPatientAddresses([]);
      setShowPatientDropdown(false);
    }
  };

  const selectPatientAddress = (address) => {
    setPatientAddressSearch(address);
    setValue('patient_address', address);
    setShowPatientDropdown(false);
    setFilteredPatientAddresses([]);
  };

  const clearPatientAddress = () => {
    setPatientAddressSearch('');
    setValue('patient_address', '');
    setShowPatientDropdown(false);
    setFilteredPatientAddresses([]);
  };

  const handleDestinationAddressSearch = (e) => {
    const value = e.target.value;
    setDestinationAddressSearch(value);
    
    if (value.length >= 3) {
      const filtered = hospitalAddresses
        .map(a => (typeof a === 'string' ? a : a.name))
        .filter(name => name.toLowerCase().includes(value.toLowerCase()));
      setFilteredDestinationAddresses(filtered);
      setShowDestinationDropdown(true);
    } else {
      setFilteredDestinationAddresses([]);
      setShowDestinationDropdown(false);
    }
  };

  const selectDestinationAddress = (address) => {
    setDestinationAddressSearch(address);
    setValue('destination_address', address);
    setShowDestinationDropdown(false);
    setFilteredDestinationAddresses([]);
  };

  const clearDestinationAddress = () => {
    setDestinationAddressSearch('');
    setValue('destination_address', '');
    setShowDestinationDropdown(false);
    setFilteredDestinationAddresses([]);
  };



  // Create case mutation
  const createCaseMutation = useMutation({
    mutationFn: (data) => api.post(endpoints.cases.createStage1, data),
    onSuccess: (response) => {
      console.log('✅ Frontend response:', response.data);
      
      const newCase = response.data?.data?.case || response.data?.case;
      
      if (!newCase) {
        console.error('❌ Case data not found in response:', response.data);
        toast({
          title: "Hata",
          description: "Vaka verisi alınamadı",
          variant: "destructive"
        });
        return;
      }
      
      queryClient.invalidateQueries(['cases']);

      if (isPresidencyUser) {
        const caseId = newCase.id || newCase.case?.id;
        const caseNumber = newCase.case_number || newCase.case?.case_number;
        setCreatedCaseId(caseId);
        setCreatedCaseNumber(caseNumber);
        setEk3CaseData(newCase.case || newCase);

        // If user pre-selected an EK-3 file in the form, upload it now
        if (ek3FormFile && caseId) {
          const formData = new FormData();
          formData.append('document', ek3FormFile);
          api.post(endpoints.cases.uploadEk3Document(caseId), formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          }).then(() => {
            setEk3Uploaded(true);
            toast({ title: "Vaka ve EK-3 Hazır", description: `Vaka #${caseNumber} oluşturuldu, EK-3 belgesi yüklendi.`, variant: "success" });
          }).catch(() => {
            toast({ title: "Vaka Oluşturuldu", description: `Vaka #${caseNumber} oluşturuldu ancak EK-3 yüklenemedi. Lütfen tekrar deneyin.`, variant: "destructive" });
          });
        } else {
          toast({
            title: "Vaka Başlatıldı",
            description: `Vaka #${caseNumber} oluşturuldu. Lütfen EK-3 belgesini yükleyin.`,
            variant: "success"
          });
        }
      } else {
        toast({
          title: "Vaka Başlatıldı",
          description: `Vaka #${newCase.case_number} başarıyla oluşturuldu.`,
          variant: "success"
        });
        navigate('/cases');
      }
    },
    onError: (error) => {
      toast({
        title: "Hata",
        description: error.response?.data?.message || "Vaka oluşturulurken hata oluştu",
        variant: "destructive"
      });
    }
  });

  const handleFormKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  };

  // Kurum dropdown klavye navigasyonu
  const handleInstitutionKeyDown = (event) => {
    if (!showInstitutionDropdown || filteredInstitutions.length === 0) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setInstitutionHighlightIndex(prev => 
          prev < filteredInstitutions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        event.preventDefault();
        setInstitutionHighlightIndex(prev => 
          prev > 0 ? prev - 1 : filteredInstitutions.length - 1
        );
        break;
      case 'Enter':
        event.preventDefault();
        if (institutionHighlightIndex >= 0 && institutionHighlightIndex < filteredInstitutions.length) {
          const selected = filteredInstitutions[institutionHighlightIndex];
          setSelectedBillingInstitution(selected.name);
          setInstitutionSearch(selected.name);
          setValue('case_giver_institution', selected.name);
          setShowInstitutionDropdown(false);
          setInstitutionHighlightIndex(-1);
        }
        break;
      case 'Escape':
        setShowInstitutionDropdown(false);
        setInstitutionHighlightIndex(-1);
        break;
    }
  };

  // Hasta adresi dropdown klavye navigasyonu
  const handlePatientAddressKeyDown = (event) => {
    if (!showPatientDropdown || filteredPatientAddresses.length === 0) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setPatientAddressHighlightIndex(prev => 
          prev < filteredPatientAddresses.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        event.preventDefault();
        setPatientAddressHighlightIndex(prev => 
          prev > 0 ? prev - 1 : filteredPatientAddresses.length - 1
        );
        break;
      case 'Enter':
        event.preventDefault();
        if (patientAddressHighlightIndex >= 0 && patientAddressHighlightIndex < filteredPatientAddresses.length) {
          const selected = filteredPatientAddresses[patientAddressHighlightIndex];
          setPatientAddressSearch(selected);
          setValue('patient_address', selected);
          setShowPatientDropdown(false);
          setPatientAddressHighlightIndex(-1);
        }
        break;
      case 'Escape':
        setShowPatientDropdown(false);
        setPatientAddressHighlightIndex(-1);
        break;
    }
  };

  // Gidilecek adres dropdown klavye navigasyonu
  const handleDestinationAddressKeyDown = (event) => {
    if (!showDestinationDropdown || filteredDestinationAddresses.length === 0) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setDestinationAddressHighlightIndex(prev => 
          prev < filteredDestinationAddresses.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        event.preventDefault();
        setDestinationAddressHighlightIndex(prev => 
          prev > 0 ? prev - 1 : filteredDestinationAddresses.length - 1
        );
        break;
      case 'Enter':
        event.preventDefault();
        if (destinationAddressHighlightIndex >= 0 && destinationAddressHighlightIndex < filteredDestinationAddresses.length) {
          const selected = filteredDestinationAddresses[destinationAddressHighlightIndex];
          setDestinationAddressSearch(selected);
          setValue('destination_address', selected);
          setShowDestinationDropdown(false);
          setDestinationAddressHighlightIndex(-1);
        }
        break;
      case 'Escape':
        setShowDestinationDropdown(false);
        setDestinationAddressHighlightIndex(-1);
        break;
    }
  };

  const onSubmit = (data) => {
    // Check if this is a planned case (future date/time)
    const requestDateTime = new Date(`${data.request_date}T${data.request_time}`);
    const now = new Date();
    const isPlanned = requestDateTime > now;
    
    // Prepare data based on form selections
    const submitData = {
      ...data,
      case_giver_type: caseGiverType,
      case_giver_person: caseGiverType === 'person' ? data.case_giver_person :
                         caseGiverType === 'presidency' ? selectedPresidencyHospital : null,
      case_giver_institution: caseGiverType === 'institution' ? selectedBillingInstitution : 
                              caseGiverType === 'organization' && selectedOrganization ? selectedOrganization.organization_name :
                              caseGiverType === 'presidency' && selectedPresidency ? selectedPresidency.name : null,
      case_giver_hospital: caseGiverType === 'presidency' ? selectedPresidencyHospital : null,
      case_giver_presidency_id: caseGiverType === 'presidency' && selectedPresidency ? selectedPresidency.id : null,
      billing_institution: caseGiverType === 'institution' ? selectedBillingInstitution : 
                           caseGiverType === 'organization' && selectedOrganization ? selectedOrganization.organization_name :
                           caseGiverType === 'presidency' && selectedPresidency ? selectedPresidency.name : null,
      organization_id: caseGiverType === 'organization'
        ? selectedOrganizationId || null
        : linkedOrganizationId || null,
      is_planned: isPlanned
    };

    console.log('🔍 Vaka oluşturuluyor:', {
      request_date: data.request_date,
      request_time: data.request_time,
      requestDateTime: requestDateTime.toISOString(),
      now: now.toISOString(),
      isPlanned: isPlanned
    });

    createCaseMutation.mutate(submitData);
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/cases')}
            className="text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Vakalara Dön
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Yeni Vaka Oluştur</h1>
            <p className="text-muted-foreground">
              Vaka bilgilerini girin ve başlatın
            </p>
          </div>
        </div>
      </div>

      {/* Form Content */}
      <form onSubmit={handleSubmit(onSubmit)} onKeyDown={handleFormKeyDown}>
        <Card>
          <CardHeader>
            <div className="flex items-center space-x-2">
              <FileText className="h-5 w-5 text-primary" />
              <CardTitle>Vaka Başlatma</CardTitle>
            </div>
            <CardDescription>
              Vaka bilgilerini eksiksiz doldurun
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            
            {/* Talep Bilgileri */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="request_date" className="flex items-center space-x-1">
                  <Calendar className="h-4 w-4" />
                  <span>Talep Tarihi *</span>
                </Label>
                <Input
                  type="date"
                  id="request_date"
                  {...register('request_date', { required: 'Talep tarihi gerekli' })}
                  className={errors.request_date ? 'border-destructive' : ''}
                />
                {errors.request_date && (
                  <p className="text-sm text-destructive">{errors.request_date.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="request_time" className="flex items-center space-x-1">
                  <Clock className="h-4 w-4" />
                  <span>Çağrı Saati *</span>
                </Label>
                <Input
                  type="time"
                  id="request_time"
                  {...register('request_time', { required: 'Çağrı saati gerekli' })}
                  className={errors.request_time ? 'border-destructive' : ''}
                />
                {errors.request_time && (
                  <p className="text-sm text-destructive">{errors.request_time.message}</p>
                )}
              </div>
            </div>

            <Separator />

            {/* Vakayı Veren Bilgisi */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center space-x-2">
                <User className="h-5 w-5" />
                <span>Vakayı Veren</span>
              </h3>

              {isPresidencyUser ? (
                <div className="flex items-center space-x-2">
                  <input 
                    type="radio" 
                    id="presidency" 
                    name="case_giver_type"
                    value="presidency"
                    checked={true}
                    readOnly
                    className="w-4 h-4 text-primary"
                  />
                  <Label htmlFor="presidency">Başkanlık</Label>
                </div>
              ) : (
                <div className="flex space-x-6">
                  <div className="flex items-center space-x-2">
                    <input 
                      type="radio" 
                      id="person" 
                      name="case_giver_type"
                      value="person"
                      checked={caseGiverType === 'person'}
                      onChange={(e) => {
                        setCaseGiverType(e.target.value);
                        setValue('case_giver_type', e.target.value);
                        setSelectedOrganizationId('');
                        setSelectedOrganization(null);
                      }}
                      className="w-4 h-4 text-primary"
                    />
                    <Label htmlFor="person">Kişi</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input 
                      type="radio" 
                      id="institution" 
                      name="case_giver_type"
                      value="institution"
                      checked={caseGiverType === 'institution'}
                      onChange={(e) => {
                        setCaseGiverType(e.target.value);
                        setValue('case_giver_type', e.target.value);
                        setSelectedOrganizationId('');
                        setSelectedOrganization(null);
                      }}
                      className="w-4 h-4 text-primary"
                    />
                    <Label htmlFor="institution">Kurum</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input 
                      type="radio" 
                      id="organization" 
                      name="case_giver_type"
                      value="organization"
                      checked={caseGiverType === 'organization'}
                      onChange={(e) => {
                        setCaseGiverType(e.target.value);
                        setValue('case_giver_type', e.target.value);
                      }}
                      className="w-4 h-4 text-primary"
                    />
                    <Label htmlFor="organization">Organizasyon</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input 
                      type="radio" 
                      id="presidency" 
                      name="case_giver_type"
                      value="presidency"
                      checked={caseGiverType === 'presidency'}
                      onChange={(e) => {
                        setCaseGiverType(e.target.value);
                        setValue('case_giver_type', e.target.value);
                        setSelectedPresidency(null);
                        setSelectedPresidencyHospital('');
                      }}
                      className="w-4 h-4 text-primary"
                    />
                    <Label htmlFor="presidency">112 Başkanlıkları</Label>
                  </div>
                </div>
              )}

              {caseGiverType === 'person' ? (
                <div className="space-y-2">
                  <Label htmlFor="case_giver_person">Kişi Adı</Label>
                  <Input
                    id="case_giver_person"
                    {...register('case_giver_person')}
                    placeholder="Vakayı veren kişinin adını girin"
                  />
                </div>
              ) : caseGiverType === 'organization' ? (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="organization_select">Organizasyon Seçin</Label>
                    <select
                      id="organization_select"
                      value={selectedOrganizationId}
                      onChange={(e) => handleOrganizationSelect(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    >
                      <option value="">Aktif organizasyon seçin</option>
                      {activeOrganizations.map((org) => (
                        <option key={org.id} value={org.id}>
                          {org.organization_name} ({org.organization_number})
                          {org.start_date ? ` - ${new Date(org.start_date).toLocaleDateString('tr-TR')}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>

                  {selectedOrganization && selectedOrganization.team_assignments && selectedOrganization.team_assignments.length > 0 && (
                    <div className="space-y-2">
                      <Label className="flex items-center space-x-1">
                        <CalendarDays className="h-4 w-4" />
                        <span>Organizasyon Ekibini Seçin</span>
                      </Label>
                      <select
                        onChange={(e) => {
                          const ta = selectedOrganization.team_assignments.find(t => String(t.team_id) === e.target.value);
                          if (ta) setValue('assigned_team_id', ta.team_id);
                          else setValue('assigned_team_id', '');
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      >
                        <option value="">Ekip seçin (opsiyonel)</option>
                        {selectedOrganization.team_assignments.map((ta) => (
                          <option key={ta.id} value={ta.team_id}>
                            {ta.team?.code || 'Ekip'} - {ta.team?.name || 'İsimsiz'}
                            {(ta.assignment_time || ta.end_time || ta.staff_names?.end_time)
                              ? ` (${ta.assignment_time || ''}${(ta.end_time || ta.staff_names?.end_time) ? ` - ${ta.end_time || ta.staff_names?.end_time}` : ''})`
                              : ''}
                            {ta.vehicle_plate ? ` [${ta.vehicle_plate}]` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              ) : caseGiverType === 'presidency' ? (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="presidency_select">Başkanlık Seçin</Label>
                    <select
                      id="presidency_select"
                      value={selectedPresidency?.id || ''}
                      onChange={(e) => {
                        const pres = presidencies.find(p => String(p.id) === e.target.value);
                        setSelectedPresidency(pres || null);
                        if (pres) {
                          setValue('case_giver_institution', pres.name);
                          setValue('case_giver_presidency_id', pres.id);
                        } else {
                          setValue('case_giver_institution', '');
                          setValue('case_giver_presidency_id', null);
                        }
                        setSelectedPresidencyHospital('');
                        setValue('case_giver_hospital', '');
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    >
                      <option value="">Başkanlık seçin</option>
                      {presidencies.map((pres) => (
                        <option key={pres.id} value={pres.id}>
                          {pres.name}
                        </option>
                      ))}
                    </select>
                    {presidencies.length === 0 && (
                      <p className="text-xs text-gray-500">Başkanlıklar yükleniyor...</p>
                    )}
                  </div>
                  {selectedPresidency && (
                    <div className="space-y-2">
                      <Label htmlFor="presidency_hospital_select">Hastane Seçin</Label>
                      <select
                        id="presidency_hospital_select"
                        value={selectedPresidencyHospital}
                        onChange={(e) => {
                          setSelectedPresidencyHospital(e.target.value);
                          setValue('case_giver_hospital', e.target.value);
                          setValue('case_giver_person', e.target.value);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      >
                        <option value="">Hastane seçin</option>
                        {presidencyHospitals.map((h) => (
                          <option key={h.id} value={h.name}>
                            {h.name}
                          </option>
                        ))}
                      </select>
                      {presidencyHospitals.length === 0 && (
                        <p className="text-xs text-orange-500">Bu başkanlığa ait hastane bulunamadı.</p>
                      )}
                      {selectedPresidencyHospital && (
                        <>
                          <p className="text-xs text-amber-600 flex items-center space-x-1">
                            <AlertCircle className="h-3 w-3" />
                            <span>Seçilen hastane kotasından düşülecektir.</span>
                          </p>

                          {/* EK-3 inline toggle */}
                          <div className="mt-3">
                            <button
                              type="button"
                              onClick={() => setEk3FormToggleOpen(prev => !prev)}
                              className={`w-full flex items-center justify-between px-4 py-3 rounded-lg border-2 transition-colors ${
                                ek3FormToggleOpen
                                  ? 'border-orange-400 bg-orange-50 text-orange-800'
                                  : 'border-dashed border-orange-300 bg-white text-orange-600 hover:bg-orange-50'
                              }`}
                            >
                              <div className="flex items-center space-x-2">
                                <FileText className="h-4 w-4" />
                                <span className="font-medium text-sm">EK-3 Belgesi Ekle</span>
                                {ek3FormFile && (
                                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                                    Seçildi ✓
                                  </span>
                                )}
                              </div>
                              <span className="text-xs text-gray-500">
                                {ek3FormToggleOpen ? '▲ Kapat' : '▼ Aç'}
                              </span>
                            </button>

                            {ek3FormToggleOpen && (
                              <div className="mt-2 p-3 border border-orange-200 rounded-lg bg-orange-50/30">
                                <input
                                  ref={ek3FormFileInputRef}
                                  type="file"
                                  accept="image/*,application/pdf"
                                  className="hidden"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (!file) return;
                                    if (file.size > 5 * 1024 * 1024) {
                                      toast({ title: "Hata", description: "Dosya boyutu 5MB'ı geçemez", variant: "destructive" });
                                      e.target.value = '';
                                      return;
                                    }
                                    setEk3FormFile(file);
                                  }}
                                />
                                {ek3FormFile ? (
                                  <div className="flex items-center justify-between p-2 bg-white rounded border border-orange-200">
                                    <div className="flex items-center space-x-2 min-w-0">
                                      <FileText className="h-4 w-4 text-orange-600 flex-shrink-0" />
                                      <span className="text-sm text-gray-700 truncate">{ek3FormFile.name}</span>
                                      <span className="text-xs text-gray-400 flex-shrink-0">
                                        ({(ek3FormFile.size / 1024).toFixed(0)} KB)
                                      </span>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setEk3FormFile(null);
                                        if (ek3FormFileInputRef.current) ek3FormFileInputRef.current.value = '';
                                      }}
                                      className="ml-2 text-red-500 hover:text-red-700 flex-shrink-0 text-sm"
                                    >
                                      <X className="h-4 w-4" />
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => ek3FormFileInputRef.current?.click()}
                                    className="w-full flex flex-col items-center py-4 text-orange-500 hover:text-orange-700 transition-colors"
                                  >
                                    <Upload className="h-8 w-8 mb-1" />
                                    <span className="text-sm font-medium">Dosya Seçin</span>
                                    <span className="text-xs text-gray-400 mt-0.5">PDF, JPG, PNG — Maks 5MB</span>
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-2 relative">
                  <Label htmlFor="case_giver_institution">Faturalanan Kurum</Label>
                  <div className="relative" ref={institutionDropdownRef}>
                    <Input
                      id="case_giver_institution"
                      value={institutionSearch || selectedBillingInstitution}
                      onChange={(e) => {
                        const value = e.target.value;
                        setInstitutionSearch(value);
                        setInstitutionHighlightIndex(-1);
                        if (!value) {
                          setSelectedBillingInstitution('');
                          setValue('case_giver_institution', '');
                        }
                      }}
                      onFocus={() => {
                        if (institutionSearch && filteredInstitutions.length > 0) {
                          setShowInstitutionDropdown(true);
                          setInstitutionHighlightIndex(-1);
                        }
                      }}
                      onKeyDown={handleInstitutionKeyDown}
                      placeholder="Kurum adı yazın (ilk karakterden arama başlar)"
                      autoComplete="off"
                    />
                    {showInstitutionDropdown && filteredInstitutions.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                        {filteredInstitutions.map((institution, index) => (
                          <div
                            key={institution.id}
                            className={`px-4 py-2 cursor-pointer ${
                              index === institutionHighlightIndex 
                                ? 'bg-blue-100 text-blue-900' 
                                : 'hover:bg-gray-100'
                            }`}
                            onClick={() => {
                              setSelectedBillingInstitution(institution.name);
                              setInstitutionSearch(institution.name);
                              setValue('case_giver_institution', institution.name);
                              setShowInstitutionDropdown(false);
                              setInstitutionHighlightIndex(-1);
                            }}
                            onMouseEnter={() => setInstitutionHighlightIndex(index)}
                          >
                            {institution.name}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <Separator />

            {/* Hasta Bilgileri */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center space-x-2">
                <User className="h-5 w-5" />
                <span>Hasta Bilgileri</span>
              </h3>

              {/* Uyruk Seçimi */}
              <div className="space-y-2">
                <Label>Uyruk *</Label>
                <div className="flex items-center space-x-6">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      value="TC"
                      {...register('patient_nationality_type')}
                      defaultChecked
                      className="h-4 w-4 text-primary border-gray-300 focus:ring-primary"
                    />
                    <span className="text-sm font-medium">Türk Vatandaşı</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      value="Foreign"
                      {...register('patient_nationality_type')}
                      className="h-4 w-4 text-primary border-gray-300 focus:ring-primary"
                    />
                    <span className="text-sm font-medium">Yabancı Uyruklu</span>
                  </label>
                </div>
              </div>

              {/* TC Kimlik No / Pasaport No */}
              <div className="space-y-2">
                <Label htmlFor="patient_id_number">
                  {watch('patient_nationality_type') === 'Foreign' ? 'Pasaport No *' : 'TC Kimlik No *'}
                </Label>
                <Input
                  id="patient_id_number"
                  {...register('patient_id_number', {
                    required: watch('patient_nationality_type') === 'Foreign' 
                      ? 'Pasaport numarası gerekli' 
                      : 'TC Kimlik No gerekli',
                    pattern: watch('patient_nationality_type') === 'TC' ? {
                      value: /^[1-9][0-9]{10}$/,
                      message: 'TC Kimlik No 11 haneli olmalı ve 0 ile başlamamalı'
                    } : undefined,
                    minLength: watch('patient_nationality_type') === 'Foreign' ? {
                      value: 5,
                      message: 'Pasaport numarası en az 5 karakter olmalı'
                    } : undefined,
                    maxLength: watch('patient_nationality_type') === 'Foreign' ? {
                      value: 20,
                      message: 'Pasaport numarası en fazla 20 karakter olmalı'
                    } : {
                      value: 11,
                      message: 'TC Kimlik No 11 haneli olmalıdır'
                    }
                  })}
                  placeholder={watch('patient_nationality_type') === 'Foreign' ? 'Pasaport numarasını girin' : 'TC Kimlik No girin'}
                  maxLength={watch('patient_nationality_type') === 'TC' ? 11 : 20}
                  className={errors.patient_id_number ? 'border-destructive' : ''}
                />
                {errors.patient_id_number && (
                  <p className="text-sm text-destructive">{errors.patient_id_number.message}</p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="patient_name">Hasta Adı *</Label>
                  <Input
                    id="patient_name"
                    {...register('patient_name', { required: 'Hasta adı gerekli' })}
                    className={`uppercase ${errors.patient_name ? 'border-destructive' : ''}`}
                    placeholder="Hasta adını girin"
                    style={{ textTransform: 'uppercase' }}
                    onChange={(e) => {
                      e.target.value = e.target.value.toLocaleUpperCase('tr-TR');
                      setValue('patient_name', e.target.value);
                    }}
                  />
                  {errors.patient_name && (
                    <p className="text-sm text-destructive">{errors.patient_name.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="patient_surname">Hasta Soyadı</Label>
                  <Input
                    id="patient_surname"
                    {...register('patient_surname')}
                    placeholder="Hasta soyadını girin"
                    className="uppercase"
                    style={{ textTransform: 'uppercase' }}
                    onChange={(e) => {
                      e.target.value = e.target.value.toLocaleUpperCase('tr-TR');
                      setValue('patient_surname', e.target.value);
                    }}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="patient_birth_year">Doğum Yılı</Label>
                  <Input
                    type="number"
                    id="patient_birth_year"
                    {...register('patient_birth_year', { 
                      min: { value: 1900, message: 'Geçerli bir yıl girin' },
                      max: { value: new Date().getFullYear(), message: 'Gelecek yıl olamaz' }
                    })}
                    placeholder="YYYY"
                    min="1900"
                    max={new Date().getFullYear()}
                    onBlur={handleBirthYearBlur}
                  />
                  {errors.patient_birth_year && (
                    <p className="text-sm text-destructive">{errors.patient_birth_year.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="patient_age">Hasta Yaşı</Label>
                  <Input
                    type="number"
                    id="patient_age"
                    {...register('patient_age', { 
                      min: { value: 0, message: 'Yaş 0 veya daha büyük olmalı' },
                      max: { value: 150, message: 'Geçerli bir yaş girin' }
                    })}
                    placeholder="Yaş"
                    min="0"
                    max="150"
                    onBlur={handleAgeBlur}
                  />
                  {errors.patient_age && (
                    <p className="text-sm text-destructive">{errors.patient_age.message}</p>
                  )}
                </div>
              </div>
            </div>

            <Separator />

            {/* Hasta Adresi */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center space-x-2">
                <MapPin className="h-5 w-5" />
                <span>Hasta Adresi</span>
              </h3>



              <div className="space-y-2">
                <Label htmlFor="patient_address">Hasta Adresi</Label>
                <div className="relative">
                  <div className="flex">
                    <Input
                      id="patient_address"
                      value={patientAddressSearch}
                      onChange={(e) => {
                        handlePatientAddressSearch(e);
                        setPatientAddressHighlightIndex(-1);
                      }}
                      onKeyDown={handlePatientAddressKeyDown}
                      placeholder="Hastane/adres aramak için en az 3 harf yazın..."
                      className="flex-1"
                      autoComplete="off"
                    />
                    {patientAddressSearch && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={clearPatientAddress}
                        className="ml-2 px-2"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  
                  {showPatientDropdown && filteredPatientAddresses.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
                      {filteredPatientAddresses.map((address, index) => (
                        <button
                          key={index}
                          type="button"
                          className={`w-full text-left px-3 py-2 border-b border-gray-100 last:border-b-0 text-sm ${
                            index === patientAddressHighlightIndex 
                              ? 'bg-blue-100 text-blue-900' 
                              : 'hover:bg-gray-100'
                          }`}
                          onClick={() => {
                            selectPatientAddress(address);
                            setPatientAddressHighlightIndex(-1);
                          }}
                          onMouseEnter={() => setPatientAddressHighlightIndex(index)}
                        >
                          {address}
                        </button>
                      ))}
                    </div>
                  )}
                  
                  {showPatientDropdown && patientAddressSearch.length >= 3 && filteredPatientAddresses.length === 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg px-3 py-2">
                      <p className="text-sm text-gray-500">Eşleşen adres bulunamadı</p>
                    </div>
                  )}
                </div>
                <div className="space-y-2 mt-2">
                  <Label htmlFor="patient_address_details">Detay Açıklama</Label>
                  <Textarea
                    id="patient_address_details"
                    {...register('patient_address_details')}
                    placeholder="Hasta adresi için detaylı açıklama girin..."
                    rows={3}
                    className="uppercase"
                    style={{ textTransform: 'uppercase' }}
                    onChange={(e) => {
                      e.target.value = e.target.value.toLocaleUpperCase('tr-TR');
                      setValue('patient_address_details', e.target.value);
                    }}
                  />
                </div>
              </div>
            </div>

            <Separator />

            {/* Gidilecek Adres (Hedef) */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center space-x-2">
                <Building2 className="h-5 w-5" />
                <span>Gideceği Adres</span>
              </h3>



              <div className="space-y-2">
                <Label htmlFor="destination_address">Hedef Adres</Label>
                <div className="relative">
                  <div className="flex">
                    <Input
                      id="destination_address"
                      value={destinationAddressSearch}
                      onChange={(e) => {
                        handleDestinationAddressSearch(e);
                        setDestinationAddressHighlightIndex(-1);
                      }}
                      onKeyDown={handleDestinationAddressKeyDown}
                      placeholder="Hastane/adres aramak için en az 3 harf yazın..."
                      className="flex-1"
                      autoComplete="off"
                    />
                    {destinationAddressSearch && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={clearDestinationAddress}
                        className="ml-2 px-2"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  
                  {showDestinationDropdown && filteredDestinationAddresses.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
                      {filteredDestinationAddresses.map((address, index) => (
                        <button
                          key={index}
                          type="button"
                          className={`w-full text-left px-3 py-2 border-b border-gray-100 last:border-b-0 text-sm ${
                            index === destinationAddressHighlightIndex 
                              ? 'bg-blue-100 text-blue-900' 
                              : 'hover:bg-gray-100'
                          }`}
                          onClick={() => {
                            selectDestinationAddress(address);
                            setDestinationAddressHighlightIndex(-1);
                          }}
                          onMouseEnter={() => setDestinationAddressHighlightIndex(index)}
                        >
                          {address}
                        </button>
                      ))}
                    </div>
                  )}
                  
                  {showDestinationDropdown && destinationAddressSearch.length >= 3 && filteredDestinationAddresses.length === 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg px-3 py-2">
                      <p className="text-sm text-gray-500">Eşleşen adres bulunamadı</p>
                    </div>
                  )}
                </div>
                <div className="space-y-2 mt-2">
                  <Label htmlFor="destination_address_details">Detay Açıklama</Label>
                  <Textarea
                    id="destination_address_details"
                    {...register('destination_address_details')}
                    placeholder="Gideceği adres için detaylı açıklama girin..."
                    rows={3}
                    className="uppercase"
                    style={{ textTransform: 'uppercase' }}
                    onChange={(e) => {
                      e.target.value = e.target.value.toLocaleUpperCase('tr-TR');
                      setValue('destination_address_details', e.target.value);
                    }}
                  />
                </div>
              </div>
            </div>

            <Separator />

            {/* Diğer Bilgiler */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="call_giver_authority" className="flex items-center space-x-1">
                  <User className="h-4 w-4" />
                  <span>Çağrıyı Veren Yetkili İsmi</span>
                </Label>
                <Input
                  id="call_giver_authority"
                  {...register('call_giver_authority')}
                  placeholder="Çağrıyı veren yetkili kişinin adını girin"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="requesting_authority" className="flex items-center space-x-1">
                  <User className="h-4 w-4" />
                  <span>Vakayı Alan Yetkili İsmi</span>
                </Label>
                <Input
                  id="requesting_authority"
                  {...register('requesting_authority')}
                  readOnly
                  className="bg-gray-50 cursor-not-allowed"
                  placeholder="Otomatik dolduruldu"
                />
                <p className="text-xs text-muted-foreground">
                  Bu alan otomatik olarak oturum açan kullanıcı bilgisiyle doldurulur
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="initial_description" className="flex items-center space-x-1">
                  <AlertCircle className="h-4 w-4" />
                  <span>Detay Açıklama</span>
                </Label>
                <Textarea
                  id="initial_description"
                  {...register('initial_description')}
                  placeholder="Vaka hakkında detaylı açıklama yazın..."
                  rows={4}
                  className="uppercase"
                  style={{ textTransform: 'uppercase' }}
                  onChange={(e) => {
                    e.target.value = e.target.value.toLocaleUpperCase('tr-TR');
                    setValue('initial_description', e.target.value);
                  }}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Organizasyon Bağlantısı - case_giver_type !== 'organization' ise göster */}
        {caseGiverType !== 'organization' && activeOrganizations.length > 0 && (
          <Card className="border-indigo-100">
            <CardHeader className="pb-3">
              <div className="flex items-center space-x-2">
                <BuildingOffice2Icon className="h-5 w-5 text-indigo-500" />
                <CardTitle className="text-base">Organizasyon Bağlantısı</CardTitle>
              </div>
              <CardDescription>Bu vakayı planlanan bir organizasyona bağlayın (opsiyonel)</CardDescription>
            </CardHeader>
            <CardContent>
              <select
                value={linkedOrganizationId}
                onChange={(e) => setLinkedOrganizationId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">— Organizasyon seçin (opsiyonel) —</option>
                {activeOrganizations.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.organization_name}
                    {org.organization_number ? ` (${org.organization_number})` : ''}
                    {org.start_date ? ` · ${new Date(org.start_date).toLocaleDateString('tr-TR')}` : ''}
                    {org.status === 'planned' ? ' · Planlanan' : org.status === 'active' ? ' · Aktif' : ''}
                  </option>
                ))}
              </select>
              {linkedOrganizationId && (() => {
                const org = activeOrganizations.find(o => String(o.id) === String(linkedOrganizationId));
                if (!org) return null;
                return (
                  <div className="mt-3 p-3 bg-indigo-50 rounded-lg text-sm space-y-1">
                    <p className="font-medium text-indigo-800">{org.organization_name}</p>
                    {org.start_date && <p className="text-indigo-600">📅 {new Date(org.start_date).toLocaleDateString('tr-TR')} {org.start_time ? org.start_time.slice(0,5) : ''}</p>}
                    {org.team_assignments && org.team_assignments.length > 0 && (
                      <p className="text-indigo-600">👥 {org.team_assignments.map(ta => ta.team?.code || ta.team?.name).filter(Boolean).join(', ')}</p>
                    )}
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        )}

        {/* Form Actions */}
        <div className="flex items-center justify-end space-x-3 mt-8">
          <Button type="button" variant="outline" onClick={() => navigate('/cases')}>
            İptal
          </Button>
          
          <Button
            type="submit"
            disabled={createCaseMutation.isPending}
            className="min-w-[120px]"
          >
            {createCaseMutation.isPending ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Vakayı Başlat
              </>
            )}
          </Button>
        </div>
      </form>

      {/* EK-3 Upload Phase - shown after case creation for presidency users */}
      {isPresidencyUser && createdCaseId && (
        <Card className="mt-6 border-orange-200">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <AlertCircle className="h-5 w-5 text-orange-500" />
                <div>
                  <CardTitle className="text-orange-700 text-base">
                    Vaka #{createdCaseNumber} oluşturuldu
                  </CardTitle>
                  <CardDescription className="mt-0.5">
                    EK-3 belgesini yükleyebilir veya daha sonra ekleyebilirsiniz.
                  </CardDescription>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* EK-3 Toggle Button */}
            <button
              type="button"
              onClick={() => setEk3ToggleOpen(prev => !prev)}
              className={`w-full flex items-center justify-between px-4 py-3 rounded-lg border-2 transition-colors ${
                ek3ToggleOpen
                  ? 'border-orange-400 bg-orange-50 text-orange-800'
                  : 'border-dashed border-orange-300 bg-white text-orange-600 hover:bg-orange-50'
              }`}
            >
              <div className="flex items-center space-x-2">
                <FileText className="h-4 w-4" />
                <span className="font-medium text-sm">EK-3 Belgesi Yükle</span>
                {ek3Uploaded && (
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                    Yüklendi ✓
                  </span>
                )}
              </div>
              <span className="text-xs text-gray-500">{ek3ToggleOpen ? '▲ Kapat' : '▼ Aç'}</span>
            </button>

            {/* EK-3 Upload Area - shown only when toggle is open */}
            {ek3ToggleOpen && (
              <div className="border border-orange-200 rounded-lg p-4 bg-orange-50/30">
                <Ek3DocumentUpload
                  caseId={createdCaseId}
                  currentDocument={ek3CaseData}
                  onDocumentChange={(data) => {
                    setEk3Uploaded(!!data);
                    setEk3CaseData(prev => ({ ...prev, ek3_document_filename: data?.filename }));
                    if (data) setEk3ToggleOpen(false);
                  }}
                />
              </div>
            )}

            <div className="flex items-center justify-end space-x-3 pt-1">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/cases')}
                className="text-gray-600"
              >
                {ek3Uploaded ? 'Vakaya Git' : 'Atla ve Sonra Yükle'}
              </Button>
              {ek3Uploaded && (
                <Button
                  type="button"
                  onClick={() => navigate('/cases')}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Save className="h-4 w-4 mr-2" />
                  Tamamla
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default NewCasePage; 